// src/components/Manufacturing/Manufacturing.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Home,
  Package,
  RefreshCw,
  Search,
  Download,
  CheckCircle,
  XCircle,
  Filter,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const Manufacturing = () => {
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    activated: '',
    retail: '',
    hasSubscription: '',
    dateFrom: '',
    dateTo: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    activated: 0,
    retail: 0,
    withSubscription: 0
  });
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    pages: 0,
    currentPage: 1
  });

  // Fetch kits data
  const fetchKits = async () => {
    try {
      setLoading(true);
      setError('');

      const queryParams = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.activated && { activated: filters.activated }),
        ...(filters.retail && { retail: filters.retail }),
        ...(filters.hasSubscription && { hasSubscription: filters.hasSubscription }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo })
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/manufacturing/kits?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch kits data');
      }

      const data = await response.json();
      setKits(data.kits);
      setStats(data.stats);
      setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error('Error fetching kits:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Export data
// Export data
const handleExport = async (format = 'csv') => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/manufacturing/kits/export?format=${format}`,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to export data');
    }

    if (format === 'csv') {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kits_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } else {
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data.kits, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kits_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    }
  } catch (err) {
    console.error('Error exporting data:', err);
    alert('Failed to export data: ' + err.message);
  }
};


  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.pages) return;
    setPagination(prev => ({
      ...prev,
      currentPage: newPage,
      offset: (newPage - 1) * prev.limit
    }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // useEffect hooks
  useEffect(() => {
    fetchKits();
  }, [pagination.offset, filters]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchKits();
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Navigation Bar */}
      <div style={{
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '1rem 2rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Link
              to="/dashboard"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#6b7280',
                textDecoration: 'none',
                fontSize: '0.875rem'
              }}
            >
              <Home size={16} />
              Dashboard
            </Link>
            <span style={{ color: '#d1d5db' }}>/</span>
            <span style={{ color: '#111827', fontWeight: '600' }}>Manufacturing</span>
          </div>
          <button
            onClick={() => fetchKits()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '0 2rem' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
            Manufacturing - Kit Management
          </h1>
          <p style={{ color: '#6b7280' }}>Manage and monitor all PlayerMaker kits</p>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Total Kits</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>{stats.total}</p>
              </div>
              <Package size={32} color="#6b7280" />
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Activated</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>{stats.activated}</p>
              </div>
              <CheckCircle size={32} color="#10b981" />
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Retail Kits</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6' }}>{stats.retail}</p>
              </div>
              <Package size={32} color="#8b5cf6" />
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>With Subscription</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>{stats.withSubscription}</p>
              </div>
              <CheckCircle size={32} color="#f59e0b" />
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div style={{
          backgroundColor: 'white',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          {/* Active Filters Indicator */}
          {(searchTerm || filters.activated || filters.retail || filters.hasSubscription || filters.dateFrom || filters.dateTo) && (
            <div style={{
              marginBottom: '0.75rem',
              padding: '0.5rem',
              backgroundColor: '#eff6ff',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              color: '#1e40af',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Filter size={16} />
              <span>Active filters: </span>
              {searchTerm && <span style={{ fontWeight: '500' }}>Search: "{searchTerm}"</span>}
              {filters.activated && <span style={{ fontWeight: '500' }}>• {filters.activated === 'true' ? 'Activated' : 'Not Activated'}</span>}
              {filters.retail && <span style={{ fontWeight: '500' }}>• {filters.retail === 'true' ? 'Retail' : 'Non-Retail'}</span>}
              {filters.hasSubscription && <span style={{ fontWeight: '500' }}>• {filters.hasSubscription === 'true' ? 'Has Subscription' : 'No Subscription'}</span>}
              {filters.dateFrom && <span style={{ fontWeight: '500' }}>• From: {filters.dateFrom}</span>}
              {filters.dateTo && <span style={{ fontWeight: '500' }}>• To: {filters.dateTo}</span>}
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Search */}
            <div style={{ flex: '1', minWidth: '250px' }}>
              <div style={{ position: 'relative' }}>
                <Search
                  size={20}
                  style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6b7280'
                  }}
                />
                <input
                  type="text"
                  placeholder="Search by serial number, subscription ID, or coupon..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem 0.5rem 2.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            </div>

            {/* Date Range Filters */}
            <input
              type="date"
              placeholder="From date"
              value={filters.dateFrom || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
              style={{
                padding: '0.5rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />

            <input
              type="date"
              placeholder="To date"
              value={filters.dateTo || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
              style={{
                padding: '0.5rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}
            />

            {/* Filters */}
            <select
              value={filters.activated}
              onChange={(e) => setFilters(prev => ({ ...prev, activated: e.target.value }))}
              style={{
                padding: '0.5rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white'
              }}
            >
              <option value="">All Activation Status</option>
              <option value="true">Activated</option>
              <option value="false">Not Activated</option>
            </select>

            <select
              value={filters.retail}
              onChange={(e) => setFilters(prev => ({ ...prev, retail: e.target.value }))}
              style={{
                padding: '0.5rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white'
              }}
            >
              <option value="">All Kit Types</option>
              <option value="true">Retail</option>
              <option value="false">Non-Retail</option>
            </select>

            <select
              value={filters.hasSubscription}
              onChange={(e) => setFilters(prev => ({ ...prev, hasSubscription: e.target.value }))}
              style={{
                padding: '0.5rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white'
              }}
            >
              <option value="">All Subscription Status</option>
              <option value="true">Has Subscription</option>
              <option value="false">No Subscription</option>
            </select>

            {/* Export and Clear Buttons */}
            <button
              onClick={() => {
                setFilters({
                  activated: '',
                  retail: '',
                  hasSubscription: '',
                  dateFrom: '',
                  dateTo: ''
                });
                setSearchTerm('');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              <XCircle size={16} />
              Clear Filters
            </button>

            <button
              onClick={() => handleExport('csv')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{
            backgroundColor: 'white',
            padding: '4rem',
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            <RefreshCw size={32} style={{ margin: '0 auto', animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading kits...</p>
          </div>
        ) : error ? (
          <div style={{
            backgroundColor: '#fef2f2',
            padding: '2rem',
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            <XCircle size={32} color="#ef4444" style={{ margin: '0 auto' }} />
            <p style={{ marginTop: '1rem', color: '#ef4444' }}>{error}</p>
          </div>
        ) : (
          <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                      Serial Number
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                      Activated
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                      Retail
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                      Subscription ID
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                      Subscription Plan
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                      Coupon Code
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                      Created
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                      Updated
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {kits.map((kit, index) => (
                    <tr
                      key={kit.id}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                      }}
                    >
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '500' }}>
                        {kit.serial_number}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {kit.is_subscription_activated === 1 || kit.is_subscription_activated === '1' || kit.is_subscription_activated === true ? (
                          <CheckCircle size={20} color="#10b981" />
                        ) : (
                          <XCircle size={20} color="#ef4444" />
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {kit.is_retail === 1 || kit.is_retail === '1' || kit.is_retail === true ? (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#e0e7ff',
                            color: '#4338ca',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem'
                          }}>
                            Retail
                          </span>
                        ) : (
                          <span style={{ color: '#6b7280' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#374151' }}>
                        {kit.subscription_id || '-'}
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#374151' }}>
                        {kit.subscription_plan || '-'}
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#374151' }}>
                        {kit.coupon_code || '-'}
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
                        {formatDate(kit.creation_time)}
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
                        {formatDate(kit.update_time)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderTop: '1px solid #e5e7eb'
              }}>
                <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} results
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.375rem',
                      backgroundColor: 'white',
                      cursor: pagination.currentPage === 1 ? 'not-allowed' : 'pointer',
                      opacity: pagination.currentPage === 1 ? 0.5 : 1
                    }}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                    {[...Array(Math.min(5, pagination.pages))].map((_, index) => {
                      let pageNum;
                      if (pagination.pages <= 5) {
                        pageNum = index + 1;
                      } else if (pagination.currentPage <= 3) {
                        pageNum = index + 1;
                      } else if (pagination.currentPage >= pagination.pages - 2) {
                        pageNum = pagination.pages - 4 + index;
                      } else {
                        pageNum = pagination.currentPage - 2 + index;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.375rem',
                            backgroundColor: pageNum === pagination.currentPage ? '#3b82f6' : 'white',
                            color: pageNum === pagination.currentPage ? 'white' : '#374151',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.pages}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.375rem',
                      backgroundColor: 'white',
                      cursor: pagination.currentPage === pagination.pages ? 'not-allowed' : 'pointer',
                      opacity: pagination.currentPage === pagination.pages ? 0.5 : 1
                    }}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Manufacturing;